import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { sendVerifyEmail } from 'api/verification';
import * as VerificationStep from 'assets/js/pages/account-settings/account-about/VerificationStep';
import * as UserEmailFormDialog from 'components/nonprofit-verification/UserEmailFormDialog';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentUser } from 'customHooks/useCurrentUser';
import { useInvalidateUserAndAccount } from 'customHooks/useInvalidateUserAndAccount';

const EmailVerification = () => {
    const toast = useToast();
    const { accountId } = useCurrentAccount();
    const { email, id: userId, isEmailVerified } = useCurrentUser();

    const { mutate: invalidateUserData } = useInvalidateUserAndAccount();
    const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
    const onOpenChange = (open: boolean) => {
        if (!open) {
            invalidateUserData();
        }
        setIsUpdatingEmail(open);
    };

    const { mutate, isPending: isLoading } = useMutation({
        mutationFn: () => sendVerifyEmail(userId, accountId),
        onSuccess: () => {
            toast.success('Email verification sent!');
        },
        onError: () => {
            toast.error('Something went wrong. Please try again.');
        },
    });

    return (
        <VerificationStep.Root isCompleted={isEmailVerified}>
            <VerificationStep.Heading>
                <VerificationStep.HeadingIcon />
                <VerificationStep.HeadingContent>
                    <VerificationStep.HeadingStep>Step 1</VerificationStep.HeadingStep>
                    <VerificationStep.HeadingTitle>
                        Verify your email
                        <VerificationStep.HeadingSubtitle>{email}</VerificationStep.HeadingSubtitle>
                    </VerificationStep.HeadingTitle>
                </VerificationStep.HeadingContent>
                <VerificationStep.HeadingLabel className="flex items-center gap-2 text-red-600">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    Required
                </VerificationStep.HeadingLabel>
            </VerificationStep.Heading>

            <VerificationStep.Content>
                <p className="max-w-md text-sm text-gray-700">
                    Click the button below to receive a link to verify your email address: {email}.
                    If you haven't received the email after a few minutes, please check your spam
                    folder.
                </p>
                <div className="flex flex-wrap items-center gap-4">
                    <Button variant="solid" disabled={isLoading} onClick={() => mutate()}>
                        {isLoading ? (
                            <FontAwesomeIcon className="animate-spin" icon={faSpinnerThird} />
                        ) : null}
                        Send verification email
                    </Button>
                    <UserEmailFormDialog.Root
                        key={email}
                        open={isUpdatingEmail}
                        onOpenChange={onOpenChange}
                    >
                        <UserEmailFormDialog.Trigger asChild>
                            <Button variant="outline" color={'neutral'}>
                                Update email
                            </Button>
                        </UserEmailFormDialog.Trigger>
                    </UserEmailFormDialog.Root>
                </div>
            </VerificationStep.Content>
        </VerificationStep.Root>
    );
};

export { EmailVerification };
